import React, { useEffect, useState } from 'react';
import i18n from 'i18next';
import Button from '@/Components/Button';
import Guest from '@/Layouts/Guest';
import {FormInput, PhoneOrEmailInput} from "@/Components/Controls";
import ValidationErrors from '@/Components/ValidationErrors';
import {Head, useForm, usePage} from '@inertiajs/inertia-react';
import ProductLogo from "@/Components/ProductLogo";
import AbstractSignupArtwork from "@/Components/AbstractSignupArtwork";
import { PSClickWrap } from "@pactsafe/pactsafe-react-sdk";
import FlashMessage from "@/Components/FlashMessage";

export default function Signup() {
    const [errors, setErrors] = useState([]);
    const [flashMessage, setFlashMessage] = useState("");
    const [continueButtonEnable, setContinueButtonEnable] = useState(true);
    const [continueButtonText, setContinueButtonText] = useState(i18n.t("signup.continue"));
    const [continueButtonAction, setContinueButtonAction] = useState(i18n.t("create-organization"));

    let { data, setData, processing, reset } = useForm({
        organization: {
            name: ''
        },
        user: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            agreeToTerms: ''
        }
    });

    useEffect(() => {
        return () => {
            reset('password', 'password_confirmation');
        };
    }, []);

    function onChange(name, value) {
        const newData = data;
        if (name == "name") {
            newData.organization[name] = value;
            setData({...data, organization: newData.organization});
        } else {
            newData.user[name] = value;
            setData({...data, user: newData.user});
        }
    }

    function onSignupTermsValid() {
        const updatedData = data;
        updatedData.user['agreeToTerms'] = true;
        setData({...data, user: updatedData.user});
    }

    function onSignupTermsInvalid() {
        const updatedData = data;
        updatedData.user['agreeToTerms'] = false;
        setData({...data, user: updatedData.user});
    }

    const createOrganization = () => {
        setContinueButtonEnable(false);
        setErrors([]);

        axios.post(route('organization.signup.store'), data).then(() => {
           window.location = route('verification.notice');
        }).catch((e) => {
            switch(e.response.status){
                // Existing user found
                case 302:
                    setContinueButtonText(i18n.t("signup.redirecting-button"));
                    setFlashMessage(i18n.t("signup.organization-created"));
                    setContinueButtonAction("redirect-to-dashboard");
                    setContinueButtonEnable(true);
                    return React.createElement(FlashMessage, {...flashMessage});
                break;
                case 422:
                    setErrors(e.response.data.errors);
                    setContinueButtonEnable(true);
                    break;
                default:
                    setContinueButtonEnable(true);
                    setErrors({generalError: i18n.t("signup.general-error")});
                    console.error(e.response)
                    break;
            }
        });
    }

    const submit = (e) => {
        e.preventDefault();

        if (!continueButtonEnable){
            return;
        }

        switch (continueButtonAction){
            case "create-organization":
                createOrganization();
                return;
            case "redirect-to-dashboard":
                window.location = "/dashboard";
                return;
            default:
                setErrors({generalError: "Unknown action triggered"});
                console.error("Unknown action triggered", continueButtonAction);
                return;
        }
    };

    /**
     * This function is executed every time this element is rendered.
     * This function aims to dynamically add the "data-testid" attribute
     * since the component PSClickWrap does not natively support the inclusion of custom attributes.
     * In this case we are looking for elements with the class "ps-contract-target"
     * so we get the first element from this list (which in this case is the only element
     * we have in the current layout with this class).
     * */
    const onRenderedPsClickWrap = () => {
        let psContractTarget = document.getElementsByClassName("ps-contract-target");

        if (psContractTarget.length > 0){
            psContractTarget[0].setAttribute('data-testid', "signup-checkbox-accept_contract");
        }

        psContractTarget[0].addEventListener('click', function() {
            const updatedData = data;
            updatedData.user['agreeToTerms'] = this.checked;
            setData({...data, user: updatedData.user});
        });
    }

    return (
        <Guest>
            <AbstractSignupArtwork/>
            <Head title="Signup" />
            <div className="signup">
                <ProductLogo />
                <form onSubmit={submit}>
                    <div data-testid="signup-div-form_title"
                         className="title">{i18n.t("signup.sign-up-for-trial")}</div>
                    <ValidationErrors
                        errors={errors["user.user"] ? {user: errors["user.user"]} : {}}></ValidationErrors>
                    <div className="row">
                        <FormInput error={errors["organization.name"]} name="name" isFocused={true}
                                   autoComplete="organization.name" value={data.organization.name} onChange={onChange}
                                   label={i18n.t("signup.organization-name")} className="col-12"
                                   onKeyUp={e => e.keyCode === 13 && next()}
                                   dataTestid="signup-form_input-choose_organization_name"/>
                    </div>

                    <div className="row">
                        <FormInput error={errors["user.firstName"]} name="firstName" autoComplete="user.firstName"
                                   value={data.user.firstName} onChange={onChange}
                                   label={i18n.t("signup.contact-first-name")} className="col-12"
                                   onKeyUp={e => e.keyCode === 13 && next()}
                                   dataTestid="signup-form_input-choose_first_name"/>
                    </div>

                    <div className="row">
                        <FormInput error={errors["user.lastName"]} name="lastName" autoComplete="user.lastName"
                                   value={data.user.lastName} onChange={onChange}
                                   label={i18n.t("signup.contact-last-name")} className="col-12"
                                   onKeyUp={e => e.keyCode === 13 && next()}
                                   dataTestid="signup-form_input-choose_last_name"/>
                    </div>

                    <div className="row">
                        <FormInput id={"email"} error={errors["user.email"]} name="email" autoComplete="user.email"
                                   value={data.user.email} onChange={onChange} label={i18n.t("signup.email")}
                                   className="col-12" onKeyUp={e => e.keyCode === 13 && next()}
                                   dataTestid="signup-form_input-choose_email"/>
                    </div>

                    <div className="row">
                        <PhoneOrEmailInput error={errors["user.phone"]} name="phone" autoComplete="user.phone"
                                           value={data.user.phone} onChange={onChange} label={i18n.t("signup.phone")}
                                           className="col-12" onKeyUp={e => e.keyCode === 13 && next()}
                                           dataTestid="signup-form_input-choose_phone"/>
                    </div>

                    <div className="row" data-test-id="signup-form_checkbox-contract-div">
                        <PSClickWrap
                            allowDisagreed={true}
                            onValid={onSignupTermsValid}
                            onInvalid={onSignupTermsInvalid}
                            customData={{
                                first_name: data.user.firstName,
                                last_name: data.user.lastName,
                                company_name: data.organization.name
                            }}
                            accessId={usePage().props.ironcladAccessKey}
                            groupKey={usePage().props.ironcladGroupKey}
                            signerIdSelector={"email"}
                            onRendered={onRenderedPsClickWrap}
                        />
                        <ValidationErrors
                            errors={errors["user.agreeToTerms"] ? {user: errors["user.agreeToTerms"]} : {}}></ValidationErrors>
                    </div>

                    <div className="">
                        <Button dataTestid="signup-form_submit_button-continue"
                                type="button"
                                onClick={submit}
                                data-action={continueButtonAction}
                                className={"w-100 btn " + (continueButtonEnable ? "btn-primary" : "btn-disabled")}
                                processing={processing}>
                            <span>{continueButtonText}</span>{continueButtonEnable ?
                            <i className="far fa-chevron-right"></i> : <></>}
                        </Button>
                    </div>

                    <div>
                        {errors["generalError"] &&
                            <>
                                <br/>
                                <ValidationErrors
                                    errors={[errors["generalError"]]}></ValidationErrors>
                            </>
                        }
                    </div>
                </form>
            </div>
            {flashMessage && <FlashMessage message={flashMessage} autoClose={true} fixPosition={false} type="info" /> }
        </Guest>
    );
}
